/** Shared Durable RPC protocol behavior. Callers supply their runtime-specific socket and authentication layer. */
import { Effect, Layer } from "effect"
import { type Rpc, RpcClient, type RpcGroup, RpcSerialization } from "effect/rpc"
import type { Socket } from "effect/socket"

export type DurableRpcClientOptions = Parameters<typeof RpcClient.makeProtocolSocket>[0]

/**
 * Defaults that match the server: the client pings every 5 s (answered by the DO's auto-response
 * without waking it), and the ping timeout is 3x the interval. Effect's default timeout equals the
 * interval, which kills idle connections after about 10 s.
 */
export const defaultClientOptions = {
  pingInterval: "5 seconds",
  pingTimeout: "15 seconds",
  retryTransientErrors: true,
} as const satisfies DurableRpcClientOptions

/**
 * RpcClient socket protocol with acks disabled (the server disables them too), so a pushed chunk
 * does not cause an Ack frame that would wake a hibernated DO per event. Uses
 * `RpcSerialization.json`, which the server's ping auto-response depends on.
 */
export const layerProtocolDurableObject = (options: DurableRpcClientOptions = defaultClientOptions) =>
  Layer.effect(RpcClient.Protocol)(
    Effect.map(RpcClient.makeProtocolSocket(options), (protocol) => ({ ...protocol, supportsAck: false })),
  ).pipe(Layer.provide(RpcSerialization.layerJson))

/**
 * Build an RpcClient for `group` over a Socket layer. The protocol layer is built into the
 * caller's Scope with `Layer.build`, because it must live as long as the client: providing it with
 * `Effect.provide` would close its reader fiber right after construction and every call would hang.
 *
 * Push subscriptions end when the socket reconnects (e.g. after a deploy). Resubscribe from the
 * last `seq` you saw (`Stream.retry`) and dedupe by `seq`.
 */
export const makeDurableRpcClient = <Rpcs extends Rpc.Any, E, R>(
  group: RpcGroup.RpcGroup<Rpcs>,
  socket: Layer.Layer<Socket.Socket, E, R>,
  options: DurableRpcClientOptions = defaultClientOptions,
) =>
  Effect.gen(function* () {
    const context = yield* Layer.build(layerProtocolDurableObject(options).pipe(Layer.provide(socket)))
    return yield* RpcClient.make(group).pipe(Effect.provideContext(context))
  })
